import React, { useState, useEffect, forwardRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Slide,
  Portal,
  Divider,
} from "@mui/material";
import { tokens } from "../../theme";
import { updateProduct } from "../../services/ProductService";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const SetThresholdModal = ({
  open,
  handleClose,
  product,
  onThresholdUpdated,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [threshold, setThreshold] = useState(20);
  const [phase, setPhase] = useState("idle");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Populate the form when the modal opens
  useEffect(() => {
    if (open && product) {
      // If the product already has a threshold, use it. Otherwise, default to 20 visually.
      setThreshold(
        product.lowStockThreshold != null ? product.lowStockThreshold : 20,
      );
      setPhase("idle");
      setSnackbarOpen(false);
    }
  }, [open, product]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parsedThreshold = parseInt(threshold, 10);
    if (isNaN(parsedThreshold) || parsedThreshold < 0) {
      setSnackbarMessage("Please enter a valid positive number.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setPhase("loading");

    try {
      // Merge the new threshold into the existing product data
      const updatedPayload = {
        ...product,
        lowStockThreshold: parsedThreshold,
      };

      const response = await updateProduct(product.id, updatedPayload);

      // Keep loading state visible to match modern aesthetics
      setTimeout(() => {
        setPhase("idle");

        if (onThresholdUpdated) {
          onThresholdUpdated(response.data);
        } else {
          handleClose();
        }

        setTimeout(() => {
          setSnackbarMessage("Low stock threshold updated successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }, 300);
      }, 1500);
    } catch (err) {
      console.error("Failed to update threshold: ", err);
      setPhase("idle");
      setSnackbarMessage(
        "Failed to update the low stock threshold. Please try again.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Shared input styling to match the modern aesthetic
  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        display: "none",
        WebkitAppearance: "none",
        margin: 0,
      },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (phase !== "loading") handleClose();
        }}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          sx={{ fontSize: "1.25rem", fontWeight: "700", pt: 3 }}
        >
          Set Low Stock Threshold
        </DialogTitle>
        <Divider
          sx={{
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          }}
        />
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: "16px !important" }}>
            <Box display="flex" flexDirection="column" gap="20px">
              <Typography color={colors.gray[100]} sx={{ fontSize: "0.95rem" }}>
                Specify the stock quantity at which{" "}
                <strong style={{ color: colors.greenAccent[500] }}>
                  {product?.productName}
                </strong>{" "}
                should trigger a low stock alert.
              </Typography>

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Low Stock Threshold"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                required
                inputProps={{ min: 0 }}
                sx={{ ...inputStyleProps }}
                InputProps={{
                  disableUnderline: true,
                  style: { borderRadius: "8px", fontSize: "0.95rem" },
                }}
                InputLabelProps={{ style: { fontSize: "0.95rem" } }}
              />
            </Box>
          </DialogContent>

          <Divider
            sx={{
              mb: 0,
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.05)",
            }}
          />

          <DialogActions
            sx={{
              pt: 2,
              pb: 3,
              px: 3,
              gap: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={handleClose}
              disabled={phase === "loading"}
              variant="outlined"
              sx={{
                color: colors.gray[100],
                borderColor: colors.gray[500],
                textTransform: "none",
                fontWeight: "600",
                padding: "10px 28px",
                borderRadius: "8px",
                width: { xs: "100%", sm: "auto" },
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: colors.gray[300],
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={phase === "loading"}
              variant="contained"
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: "#ffffff",
                textTransform: "none",
                fontWeight: "600",
                padding: "10px 32px",
                borderRadius: "8px",
                width: { xs: "100%", sm: "auto" },
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 4px 10px rgba(0,0,0,0.3)"
                    : "0 4px 12px rgba(79, 70, 229, 0.2)",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: colors.blueAccent[500],
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 6px 14px rgba(0,0,0,0.4)"
                      : "0 6px 16px rgba(79, 70, 229, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.12)"
                      : "rgba(0, 0, 0, 0.12)",
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.3)"
                      : "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              {phase === "loading" ? (
                <>
                  Saving&nbsp;
                  <CircularProgress
                    size={18}
                    sx={{ color: "inherit", ml: "8px" }}
                  />
                </>
              ) : (
                "Save Threshold"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#fff",
              fontWeight: "600",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default SetThresholdModal;
